/**
 * Teacher Payout Page - Allows teachers to request payouts for completed sessions
 * Shows eligible bookings, payout history, and allows submitting payout requests
 */

import React, { useState, useEffect } from 'react';
import { FaMoneyBillWave, FaSpinner, FaCheckCircle, FaHistory, FaClock, FaUniversity } from 'react-icons/fa';
import {
  getEligiblePayoutBookings,
  getPayoutHistory,
  requestTeacherPayout,
  formatCurrency
} from './paymentService';

const TeacherPayoutPage = () => {
  const [eligibleBookings, setEligibleBookings] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [payoutMethod, setPayoutMethod] = useState('bank_transfer');
  const [bankDetails, setBankDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    iban: '',
    swiftCode: ''
  });

  useEffect(() => {
    loadPayoutData();
  }, []);

  const loadPayoutData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [bookingsResult, historyResult] = await Promise.all([
        getEligiblePayoutBookings(),
        getPayoutHistory()
      ]);

      if (bookingsResult.success) {
        setEligibleBookings(bookingsResult.bookings);
      }

      if (historyResult.success) {
        setPayoutHistory(historyResult.payouts);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading payout data:', err);
      setError('Failed to load payout data');
      setLoading(false);
    }
  };

  const handleSelectBooking = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedBookings.length === eligibleBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(eligibleBookings.map(b => b.id));
    }
  };

  const calculateTotalPayout = () => {
    return selectedBookings.reduce((total, bookingId) => {
      const booking = eligibleBookings.find(b => b.id === bookingId);
      return total + (parseFloat(booking?.teacher_payout_amount) || 0);
    }, 0);
  };

  const handleSubmitPayout = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      // Validate form
      if (selectedBookings.length === 0) {
        throw new Error('Please select at least one session');
      }

      if (payoutMethod === 'bank_transfer') {
        if (!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.bankName) {
          throw new Error('Please fill in all required bank details');
        }
      }

      const result = await requestTeacherPayout(
        selectedBookings,
        payoutMethod,
        bankDetails
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      setSuccess(`Payout request submitted successfully! Total: ${formatCurrency(calculateTotalPayout())}`);
      setSelectedBookings([]);
      setShowPayoutForm(false);
      setBankDetails({
        accountName: '',
        accountNumber: '',
        bankName: '',
        iban: '',
        swiftCode: ''
      });

      // Reload data
      await loadPayoutData();
      setSubmitting(false);
    } catch (err) {
      console.error('Payout submission error:', err);
      setError(err.message || 'Failed to submit payout request');
      setSubmitting(false);
    }
  };

  const getPayoutStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.pending;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-5xl text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading payout data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <FaMoneyBillWave className="mr-3 text-green-500" />
                Teacher Payouts
              </h1>
              <p className="text-gray-600 mt-2">Request payouts for your completed sessions</p>
            </div>
            {selectedBookings.length > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-600">Selected Payout</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(calculateTotalPayout())}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6 flex items-center">
            <FaCheckCircle className="text-green-500 text-2xl mr-3" />
            <p className="text-green-800 font-semibold">{success}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-800 font-semibold">{error}</p>
          </div>
        )}

        {/* Eligible Sessions */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Eligible Sessions</h2>
            {eligibleBookings.length > 0 && (
              <div className="flex gap-3">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  {selectedBookings.length === eligibleBookings.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedBookings.length > 0 && (
                  <button
                    onClick={() => setShowPayoutForm(true)}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-emerald-700 transition shadow-lg"
                  >
                    Request Payout ({selectedBookings.length} sessions)
                  </button>
                )}
              </div>
            )}
          </div>

          {eligibleBookings.length === 0 ? (
            <div className="text-center py-12">
              <FaMoneyBillWave className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No eligible sessions for payout</p>
              <p className="text-gray-400 text-sm mt-2">
                Completed and paid sessions will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {eligibleBookings.map((booking) => (
                <div
                  key={booking.id}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition ${
                    selectedBookings.includes(booking.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                  onClick={() => handleSelectBooking(booking.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        checked={selectedBookings.includes(booking.id)}
                        onChange={() => handleSelectBooking(booking.id)}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500 mr-4"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <p className="font-bold text-gray-800">{booking.student_name}</p>
                          <span className="text-sm text-gray-500">{formatDate(booking.date)}</span>
                          <span className="text-sm text-gray-500">
                            {booking.time_from} - {booking.time_to}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {booking.request_type} • {booking.num_students} student(s)
                        </p>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(parseFloat(booking.teacher_payout_amount))}
                      </p>
                      <p className="text-xs text-gray-500">Teacher payout</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payout Form Modal */}
        {showPayoutForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Submit Payout Request</h2>

              <div className="bg-green-50 rounded-xl p-4 mb-6 border-2 border-green-200">
                <p className="text-lg font-semibold text-gray-800">
                  Total Payout: <span className="text-green-600">{formatCurrency(calculateTotalPayout())}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedBookings.length} session(s) selected
                </p>
              </div>

              {/* Payout Method */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Payout Method
                </label>
                <select
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="benefit_pay">Benefit Pay</option>
                </select>
              </div>

              {/* Bank Details Form */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account Holder Name *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.accountName}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                    placeholder="Full name as on bank account"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.accountNumber}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                    placeholder="Bank account number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.bankName}
                    onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                    placeholder="Name of your bank"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    IBAN (Optional)
                  </label>
                  <input
                    type="text"
                    value={bankDetails.iban}
                    onChange={(e) => setBankDetails({ ...bankDetails, iban: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                    placeholder="International bank account number"
                  />
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Payout requests are processed manually by our team.
                  You will receive the funds within 3-5 business days after approval.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowPayoutForm(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPayout}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Payout Request'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payout History */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <FaHistory className="mr-3 text-orange-500" />
            Payout History
          </h2>

          {payoutHistory.length === 0 ? (
            <div className="text-center py-12">
              <FaClock className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No payout history yet</p>
              <p className="text-gray-400 text-sm mt-2">
                Your payout requests will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {payoutHistory.map((payout, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-xl p-4 hover:border-gray-300 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getPayoutStatusBadge(payout.payout_status)}`}>
                          {payout.payout_status.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500">{formatDate(payout.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Method: <strong>{payout.payout_method.replace('_', ' ')}</strong>
                      </p>
                      {payout.processed_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Processed: {formatDate(payout.processed_at)}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-gray-800">
                        {formatCurrency(parseFloat(payout.total_amount))}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherPayoutPage;
